import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { formatCurrency, formatCurrencyFull, formatNumber } from '../lib/utils';
import { Loader2, TrendingUp, Globe, Music2, BarChart3 } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TrackAnalyticsModal = ({ open, onOpenChange, trackName, labelId = null }) => {
    const [loading, setLoading] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const token = localStorage.getItem('token');

    useEffect(() => {
        if (open && trackName) {
            fetchAnalytics();
        }
    }, [open, trackName]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/dashboard/track-analytics/${encodeURIComponent(trackName)}`;
            if (labelId) {
                url += `?label_id=${labelId}`;
            }

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Failed to fetch track analytics');

            const data = await response.json();
            setAnalytics(data);
        } catch (error) {
            toast.error('Failed to load track analytics');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !analytics) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="track-analytics-loading">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Music2 className="w-5 h-5 text-primary" />
                            {trackName || 'Track Analytics'}
                        </DialogTitle>
                        <DialogDescription>Loading track-level performance…</DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="track-analytics-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Music2 className="w-5 h-5 text-primary" />
                        {analytics?.track_info?.title || trackName || 'Track Analytics'}
                    </DialogTitle>
                    <DialogDescription>
                        {analytics?.track_info?.artist}
                        {analytics?.track_info?.label ? ` • ${analytics.track_info.label}` : ''}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                            <CardContent className="pt-4">
                                <div className="text-2xl font-bold text-primary">
                                    {formatNumber(analytics?.key_metrics?.total_streams ?? 0)}
                                </div>
                                <div className="text-sm text-muted-foreground">Total Streams</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
                            <CardContent className="pt-4">
                                <div className="text-2xl font-bold text-green-500">
                                    {formatCurrency(analytics?.key_metrics?.total_revenue_inr ?? 0, 'INR')}
                                </div>
                                <div className="text-sm text-muted-foreground">Total Revenue</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                            <CardContent className="pt-4">
                                <div className="text-2xl font-bold text-blue-500">
                                    ₹{Number(analytics?.key_metrics?.avg_per_stream_inr ?? 0).toFixed(4)}
                                </div>
                                <div className="text-sm text-muted-foreground">Avg per Stream</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
                            <CardContent className="pt-4">
                                <div className="text-2xl font-bold text-amber-500">
                                    {analytics?.key_metrics?.record_count ?? 0}
                                </div>
                                <div className="text-sm text-muted-foreground">Revenue Records</div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Monthly Trend */}
                    {analytics.monthly_trend && analytics.monthly_trend.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-primary" />
                                    Monthly Trend
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={analytics.monthly_trend}>
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                        <XAxis 
                                            dataKey="month" 
                                            tick={{ fontSize: 12 }}
                                            className="text-muted-foreground"
                                        />
                                        <YAxis 
                                            tick={{ fontSize: 12 }}
                                            className="text-muted-foreground"
                                        />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '8px'
                                            }}
                                            formatter={(value, name) => [
                                                name === 'revenue_inr' ? formatCurrency(value, 'INR') : formatNumber(value),
                                                name === 'revenue_inr' ? 'Revenue' : 'Streams'
                                            ]}
                                        />
                                        <Legend />
                                        <Line 
                                            type="monotone" 
                                            dataKey="streams" 
                                            stroke="hsl(var(--primary))" 
                                            strokeWidth={2}
                                            name="Streams"
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="revenue_inr" 
                                            stroke="hsl(142 76% 36%)" 
                                            strokeWidth={2}
                                            name="Revenue (INR)"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Platform Breakdown */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-primary" />
                                    Platform Breakdown
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {analytics.platform_breakdown && analytics.platform_breakdown.length > 0 ? (
                                    <>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <BarChart data={analytics.platform_breakdown.slice(0, 5)}>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                                <XAxis 
                                                    dataKey="platform" 
                                                    tick={{ fontSize: 11 }}
                                                    angle={-15}
                                                    textAnchor="end"
                                                    height={60}
                                                />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))',
                                                        border: '1px solid hsl(var(--border))',
                                                        borderRadius: '8px'
                                                    }}
                                                    formatter={(value) => formatCurrencyFull(value, 'INR')}
                                                />
                                                <Bar dataKey="revenue_inr" fill="hsl(var(--primary))" />
                                            </BarChart>
                                        </ResponsiveContainer>

                                        <div className="mt-4 border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Platform</TableHead>
                                                        <TableHead className="text-right">Streams</TableHead>
                                                        <TableHead className="text-right">Revenue</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {analytics.platform_breakdown.map((platform, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell className="font-medium">{platform.platform}</TableCell>
                                                            <TableCell className="text-right">{formatNumber(platform.streams)}</TableCell>
                                                            <TableCell className="text-right font-semibold text-green-500">
                                                                {formatCurrency(platform.revenue_inr, 'INR')}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No platform data available
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Geographic Breakdown */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Globe className="w-5 h-5 text-primary" />
                                    Geographic Breakdown
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {analytics.geo_breakdown && analytics.geo_breakdown.length > 0 ? (
                                    <>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <BarChart data={analytics.geo_breakdown.slice(0, 5)}>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                                <XAxis 
                                                    dataKey="territory" 
                                                    tick={{ fontSize: 11 }}
                                                    angle={-15}
                                                    textAnchor="end"
                                                    height={60}
                                                />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))',
                                                        border: '1px solid hsl(var(--border))',
                                                        borderRadius: '8px'
                                                    }}
                                                    formatter={(value) => formatCurrencyFull(value, 'INR')}
                                                />
                                                <Bar dataKey="revenue_inr" fill="hsl(var(--primary))" />
                                            </BarChart>
                                        </ResponsiveContainer>

                                        <div className="mt-4 border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Territory</TableHead>
                                                        <TableHead className="text-right">Streams</TableHead>
                                                        <TableHead className="text-right">Revenue</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {analytics.geo_breakdown.map((geo, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell className="font-medium">{geo.territory}</TableCell>
                                                            <TableCell className="text-right">{formatNumber(geo.streams)}</TableCell>
                                                            <TableCell className="text-right font-semibold text-green-500">
                                                                {formatCurrency(geo.revenue_inr, 'INR')}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No geographic data available
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TrackAnalyticsModal;
